import { type Node } from '@xyflow/react'
import { Search } from 'lucide-react'
import React, { useState, useEffect, useMemo, memo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'

const nodeTypeNames = {
  annotationnode: 'Annotation Node',
  ghost: 'Ghost Node',
  nodecustom: 'Custom Node',
  randomsequencenode: 'Random Sequence Node',
  nodeknowledgeextraction: 'Knowledge Extraction Node',
  nodedocumenttemplate: 'Document Template Node',
  nodequickstart: 'Quick Start Node',
  nodedocument: 'Document Node',
  nodechat: 'Chat Node',
  nodedocumentdesigner: 'Document Designer',
  NodeIntellica: 'Intellica',
  nodecomparefiles: 'Compare Files Report Node',
  nodesmartnotes: 'Smart Notes Node',
  nodediagrams: 'PlantUML Diagram Node',
  noderesearcher: 'Researcher Node',
}

export interface NodeIndexDialogProps {
  visible: boolean
  onHide: () => void
  nodes: Node[]
  onNodeSelect: (nodeId: string) => void
}

const NodeIndexDialog = (props: NodeIndexDialogProps) => {
  const { visible, onHide, nodes, onNodeSelect } = props
  const [searchTerm, setSearchTerm] = useState('')
  const [groupedNodes, setGroupedNodes] = useState<Record<string, Node[]>>({})
  const [isDarkMode, setIsDarkMode] = useState(false)

  useEffect(() => {
    // Group nodes by type
    const grouped = nodes.reduce(
      (acc, node) => {
        const type = node.type || 'default'
        if (!acc[type]) acc[type] = []
        acc[type].push(node)
        return acc
      },
      {} as Record<string, Node[]>
    )
    setGroupedNodes(grouped)
  }, [nodes])

  // Update nodes groups whenever nodes state changes
  const filteredGroups = useMemo(() => {
    const term = searchTerm.toLowerCase()
    const filtered: Record<string, Node[]> = {}

    Object.entries(groupedNodes).forEach(([type, nodes]) => {
      const matchingNodes = nodes.filter(
        (node) =>
          node.data?.label?.toLowerCase().includes(term) ||
          type.toLowerCase().includes(term)
      )
      if (matchingNodes.length > 0) {
        filtered[type] = matchingNodes
      }
    })

    return filtered
  }, [groupedNodes, searchTerm])

  const getReadableName = (type: string) => {
    return nodeTypeNames[type] || 'Unknown Node Type'
  }

  const checkDarkMode = () => {
    // Check for dark mode on component mount
    setIsDarkMode(
      document.documentElement.getAttribute('data-theme') === 'dark'
    )
  }

  useEffect(() => {
    // Initial check
    checkDarkMode()

    // Create an observer to detect theme changes
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect() // Cleanup observer on unmount
  }, [])

  // Render dialog box with project items
  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>Current Nodes</DialogTitle>
          <DialogDescription>Listing all available nodes</DialogDescription>
        </DialogHeader>
        <div className="p-3">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search nodes..."
                className="pl-8"
              />
            </div>
            <Button variant="outline" onClick={() => setSearchTerm('')}>
              Clear
            </Button>
          </div>
          <ScrollArea className="h-[60vh]">
            {Object.entries(filteredGroups).map(([type, nodes]) => (
              <div key={type} className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                  {getReadableName(type) + '(s)'}
                </h3>
                <div className="space-y-2">
                  {nodes.map((node) => (
                    <Button
                      key={node.id}
                      onClick={() => onNodeSelect(node.id)}
                      className="p-2 m-1 w-90vw text-left justify-start"
                      variant={isDarkMode ? 'outline' : 'default'}
                    >
                      {node.data?.label || node.id}
                    </Button>
                  ))}
                </div>
              </div>
            ))}

            <Button
              onClick={() => setSearchTerm('')}
              className="mt-2 w-full"
              variant="outline"
            >
              <Search className="mr-2 h-4 w-4" />
              Reset Search
            </Button>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default memo(NodeIndexDialog)
